import { useEffect, useMemo, useState, useCallback } from "react";
import dayjs from "dayjs";
import {
  Box,
  Typography,
  Switch,
  FormControlLabel,
  Button,
  Chip,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import Ghost from "../../views/ghost/Ghost";
import { useErrorBoundary } from "react-error-boundary";
import { Statuses } from "../../enums/Statuses";
import TipsService from "../../services/tips.service";
import classes from "./SingleTip.module.scss";
import { Tip } from "../../interfaces/Tip";
import InformationSection from "../../views/Information-section/InformationSection";
import DateHelper from "../../helpers/date.helper";
import { VariationTip } from "../../interfaces/VariationTip";
import VariationTipCard from "../../views/variation-tip-card/VariationTipCard";
import NotFound from "../../pages/not-found/NotFound";
import AddIcon from "@mui/icons-material/Add";
import TipVariationsModal from "../tip-variation-modal/TipVariationsModal";
import Tags from "../../views/tags/Tags";
import { useDispatch, useSelector } from "react-redux";
import {
  setTipsAuthors,
  setTipsTags,
} from "../../../store/filters/tipFilters.slice";
import { Tag } from "../../interfaces/Tag";
import { RootState } from "../../../store";
import AuthorInfoPopup from "../../views/authorPopup/AuthorPopup";
import TipLength from "../tip-length/TipLength";

const SingleTip = () => {
  const { id } = useParams();
  const [tip, setTip] = useState<Tip | null>(null);
  const [status, setStatus] = useState<Statuses>(Statuses.IDLE);
  const { showBoundary } = useErrorBoundary();
  const [showRawAnalysis, setShowRawAnalysis] = useState<boolean>(false);
  const [showAuthorInfo, setShowAuthorInfo] = useState<boolean>(false);
  const handleToggleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setShowRawAnalysis(event.target.checked);
    },
    []
  );
  const [openModal, setOpenModal] = useState<boolean>(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const currentTags = useSelector(
    (state: RootState) => state.tipFilters.tipFilters.tags || []
  );
  const currentAuthors = useSelector(
    (state: RootState) => state.tipFilters.tipFilters.authors || []
  );

  const calculateTimeDifference = useMemo(() => {
    return DateHelper.calculateTimeDifferenceFromNow(
      dayjs(tip?.attributes.createdAt)
    );
  }, [tip?.attributes.createdAt]);

  useEffect(() => {
    if (!id) {
      return;
    }

    const abortController = new AbortController();
    setStatus(Statuses.PENDING);
    TipsService.getTipById(id)
      .then((data) => {
        setTip(data);
        setStatus(Statuses.SUCCESS);
      })
      .catch((error: Error) => {
        setStatus(Statuses.ERROR);
        showBoundary(error);
      });

    return () => abortController.abort();
  }, [id, showBoundary]);

  const handleTagClick = useCallback(
    (tag: Tag) => {
      const isTagAlreadySelected = currentTags.some(
        (existingTag) => existingTag.id === tag.id
      );

      if (!isTagAlreadySelected) {
        const updatedTags = [...currentTags, tag];
        dispatch(setTipsTags(updatedTags));
        navigate(`/`);
      }
    },
    [dispatch, navigate, currentTags]
  );

  const handleAuthorClick = useCallback(() => {
    if (tip?.attributes.author.data) {
      const isAuthorAlreadySelected = currentAuthors.some(
        (existingAuthor) =>
          existingAuthor.id === tip?.attributes?.author?.data?.id
      );

      if (!isAuthorAlreadySelected) {
        const updatedAuthors = [...currentAuthors, tip.attributes.author.data];
        dispatch(setTipsAuthors(updatedAuthors));
        navigate(`/`);
      }
    }
  }, [dispatch, navigate, tip, currentAuthors]);

  const renderBettingInfo = useMemo(() => {
    if (!tip?.attributes.betOption) {
      return null;
    }

    return (
      <Box className={`${classes["article-box"]} p-4`}>
        <Typography className="h4 mb-3">Betting Information</Typography>
        <Box className="d-flex flex-column gap-3">
          <Box className="d-flex flex-wrap gap-2">
            {tip.attributes.betOption?.usOdds && (
              <Chip
                label={`US Odds: ${tip.attributes.betOption.usOdds}`}
                variant="outlined"
                color="primary"
                size="medium"
                className={classes.oddsChip}
              />
            )}
            {tip.attributes.betOption?.decimalOdds && (
              <Chip
                label={`Decimal Odds: ${tip.attributes.betOption.decimalOdds}`}
                variant="outlined"
                color="secondary"
                size="medium"
                className={classes.oddsChip}
              />
            )}
          </Box>
          
          <Box className="d-flex flex-column gap-2">
            {tip.attributes.betOption?.betType && (
              <Typography variant="body1">
                <strong>Bet Type:</strong> {tip.attributes.betOption.betType}
              </Typography>
            )}
            {tip.attributes.betOption?.lineType && (
              <Typography variant="body1">
                <strong>Line Type:</strong> {tip.attributes.betOption.lineType}
              </Typography>
            )}
            {tip.attributes.betOption?.line && (
              <Typography variant="body1">
                <strong>Line:</strong> {tip.attributes.betOption.line}
              </Typography>
            )}
            {tip.attributes.betOption?.optionType && (
              <Typography variant="body1">
                <strong>Option Type:</strong> {tip.attributes.betOption.optionType}
              </Typography>
            )}
            {tip.attributes.betOption?.periodType && (
              <Typography variant="body1">
                <strong>Period:</strong> {tip.attributes.betOption.periodType}
              </Typography>
            )}
            {tip.attributes.betOption?.weight && (
              <Typography variant="body1">
                <strong>Weight:</strong> {tip.attributes.betOption.weight}
              </Typography>
            )}
          </Box>
        </Box>
      </Box>
    );
  }, [tip]);

  const renderTipData = useMemo(() => {
    if (!tip) {
      return null;
    }

    return (
      <Box
        className={`${classes["article-box"]} p-1 p-sm-4 d-flex flex-column gap-4 container`}
      >
        <Box className="d-flex justify-content-between align-items-start">
          <Box className="position-relative">
            <Typography
              color="text.disabled"
              variant="caption"
              className={
                tip.attributes.author.data?.attributes.name
                  ? `${classes.authorText} ${classes.authorLink}`
                  : ""
              }
              onMouseEnter={() => setShowAuthorInfo(true)}
              onMouseLeave={() => setShowAuthorInfo(false)}
              onClick={handleAuthorClick}
            >
              By{" "}
              {tip.attributes.author.data?.attributes.name ?? "Unknown Author"}
            </Typography>
            {showAuthorInfo && tip.attributes.author.data?.attributes.name && (
              <AuthorInfoPopup author={tip.attributes.author.data} />
            )}
          </Box>
          <Typography alignSelf="start" color="grey">
            {calculateTimeDifference}
          </Typography>
        </Box>
        <Typography variant="h4">{tip.attributes.englishTitle}</Typography>
        <Typography variant="body1">{tip.attributes.summary}</Typography>
        <Box className="d-flex flex-column gap-2">
          <InformationSection
            title="Facts"
            content={tip.attributes.facts ?? ""}
            biggerFont={true}
          />
          <Box className="d-flex justify-content-center justify-content-sm-end my-3 my-sm-0 ">
            <Button
              className={`${classes["variation-button"]} p-2`}
              disableElevation
              variant="contained"
              onClick={() => setOpenModal(true)}
            >
              <AddIcon fontSize="small" />
              Create Variation
            </Button>
          </Box>
        </Box>
      </Box>
    );
  }, [tip, handleAuthorClick, calculateTimeDifference, showAuthorInfo]);

  const renderAnalysis = useMemo(() => {
    if (!tip) {
      return null;
    }

    return (
      <Box className={`${classes["article-box"]} p-4 overflow-hidden`}>
        <Box className="d-flex justify-content-between align-items-center mb-3">
          <Box className="d-flex align-items-center gap-3">
            <Typography className="h4">Tip Analysis</Typography>
            {tip.attributes.numberOfWords !== null && (
              <TipLength numberOfWords={tip.attributes.numberOfWords ?? 0} />
            )}
          </Box>
          <FormControlLabel
            label={"Show Raw Analysis"}
            control={
              <Switch
                checked={showRawAnalysis}
                onChange={handleToggleChange}
                classes={{ checked: "switch", track: "switch-base" }}
              />
            }
            labelPlacement="start"
          />
        </Box>
        <Typography variant="h6" fontWeight="bold" gutterBottom>
          {showRawAnalysis ? "Raw Analysis:" : "English Analysis:"}
        </Typography>
        <Typography>
          {showRawAnalysis
            ? tip.attributes.rawAnalysis
            : tip.attributes.translatedAnalysis}
        </Typography>
      </Box>
    );
  }, [tip, showRawAnalysis, handleToggleChange]);

  if (tip === null && status === Statuses.ERROR) {
    return <NotFound />;
  }

  return (
    <Box className="container mt-5 d-flex flex-column gap-4">
      {status === Statuses.PENDING ? (
        <Box className="boxed d-flex flex-column gap-4">
          <Ghost className="article" />
          <Ghost className="variation-tip" />
          <Ghost className="variation-tip" />
        </Box>
      ) : (
        <>
          {renderTipData}
          {renderBettingInfo}
          {tip && tip.attributes.tags.data?.length !== 0 && (
            <Box className={`${classes["article-box"]} p-4`}>
              {" "}
              <Typography className="h4 ps-1 pb-4">
                Article Tags
              </Typography>{" "}
              <Tags
                tags={tip.attributes.tags.data ?? []}
                onTagClick={(tag) => handleTagClick(tag)}
              />{" "}
            </Box>
          )}
          {renderAnalysis}
          <Box className={`${classes.box} py-4 px-4`}>
            <Typography className="h4">
              Variations ({tip && tip.attributes.variation_tips.data?.length})
            </Typography>
            {tip &&
              tip.attributes.variation_tips.data?.map(
                (variationTip: VariationTip) => (
                  <Box
                    key={variationTip.id}
                    className={`${classes.box} p-0 p-sm-4`}
                  >
                    <VariationTipCard variation={variationTip} />
                  </Box>
                )
              )}
          </Box>
        </>
      )}
      {tip && (
        <TipVariationsModal
          isOpen={openModal}
          onClose={() => setOpenModal(false)}
          tip={tip}
        />
      )}
    </Box>
  );
};

export default SingleTip;
